
import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";

// Production API URL
const API_URL =    process.env.REACT_APP_API_URL || "https://eshaastra.in/api";

const DangerZone = () => {

    // =====================================================
    // STATES
    // =====================================================

    const [models, setModels] = useState([]);

    const [summary, setSummary] = useState({
        totalCollections: 0,
        totalDocuments: 0,
        totalSize: 0
    });

    const [loading, setLoading] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [exporting, setExporting] = useState("");

    const token = localStorage.getItem("token");

    // =====================================================
    // COMMON API HEADERS
    // =====================================================

    const getHeaders = () => {
        return {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
        };
    };

    // =====================================================
    // FORMAT FILE SIZE
    // =====================================================

    const formatSize = (bytes = 0) => {

        if (!bytes || bytes <= 0) return "0 Bytes";

        const units = ["Bytes", "KB", "MB", "GB", "TB"];

        const index = Math.floor(
            Math.log(bytes) / Math.log(1024)
        );

        const size = bytes / Math.pow(1024, index);

        return `${size.toFixed(2)} ${units[index] || "TB"}`;
    };

    // =====================================================
    // LOAD DATABASE STATISTICS
    // GET /api/danger-zone/stats
    // =====================================================

    const loadModels = useCallback(async (showError = true) => {

        try {

            setLoading(true);

            if (!token) {
                throw new Error("Authentication token not found. Please login again.");
            }

            const response = await fetch(
                `${API_URL}/danger-zone/stats`,
                {
                    method: "GET",
                    headers: getHeaders(),
                    cache: "no-store"
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(
                    data.message || "Unable to load database statistics"
                );
            }

            setModels(Array.isArray(data.data) ? data.data : []);

            setSummary({
                totalCollections:
                    data.summary?.totalCollections ?? data.data?.length ?? 0,

                totalDocuments:
                    data.summary?.totalDocuments ?? 0,

                totalSize:
                    data.summary?.totalSize ?? 0
            });

            return true;

        } catch (error) {

            console.error("Database statistics error:", error);

            if (showError) {
                Swal.fire(
                    "Error",
                    error.message || "Unable to load database statistics",
                    "error"
                );
            }

            return false;

        } finally {

            setLoading(false);

        }

    }, [token]);

    // =====================================================
    // INITIAL LOAD
    // =====================================================

    useEffect(() => {

        loadModels();

    }, [loadModels]);

    // =====================================================
    // EXPORT DATABASE
    // JSON / CSV / ZIP
    // =====================================================

    const handleExport = async (format) => {

        try {

            if (!token) {
                throw new Error("Authentication token not found.");
            }

            setExporting(format);

            const response = await fetch(
                `${API_URL}/danger-zone/export?format=${format}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    cache: "no-store"
                }
            );

            if (!response.ok) {

                let message = "Database export failed";

                try {
                    const errorData = await response.json();
                    message = errorData.message || message;
                } catch {
                    // Response was not JSON
                }

                throw new Error(message);
            }

            const blob = await response.blob();

            if (!blob.size) {
                throw new Error("The exported file is empty.");
            }

            const timestamp = new Date()
                .toISOString()
                .replace(/[:.]/g, "-");

            const filename =
                `mongodb-backup-${timestamp}.${format}`;

            const downloadUrl =
                window.URL.createObjectURL(blob);

            const link = document.createElement("a");

            link.href = downloadUrl;
            link.download = filename;

            document.body.appendChild(link);

            link.click();

            link.remove();

            window.setTimeout(() => {
                window.URL.revokeObjectURL(downloadUrl);
            }, 1000);

            Swal.fire({
                icon: "success",
                title: "Export Successful",
                text: `${format.toUpperCase()} backup downloaded successfully.`,
                timer: 2000,
                showConfirmButton: false
            });

        } catch (error) {

            console.error("Export error:", error);

            Swal.fire(
                "Export Failed",
                error.message || "Unable to export database",
                "error"
            );

        } finally {

            setExporting("");

        }
    };

    // =====================================================
    // EMPTY INDIVIDUAL COLLECTION
    // POST /api/danger-zone/empty
    // =====================================================

    const handleEmptyModel = async (model) => {

        const result = await Swal.fire({

            title: "Empty Collection?",

            text: `You are about to permanently delete all records from ${model}. This action cannot be undone.`,

            icon: "warning",

            input: "text",

            inputLabel: "Type DELETE to confirm",

            inputPlaceholder: "Type DELETE",

            inputAttributes: {
                autocapitalize: "off",
                autocomplete: "off"
            },

            showCancelButton: true,

            confirmButtonText: "Delete Everything",

            confirmButtonColor: "#dc3545",

            cancelButtonText: "Cancel",

            inputValidator: (value) => {

                if (value !== "DELETE") {
                    return "Please type DELETE exactly";
                }

                return null;
            }

        });

        if (!result.isConfirmed) return;

        try {

            setResetting(true);

            const response = await fetch(
                `${API_URL}/danger-zone/empty`,
                {
                    method: "POST",

                    headers: getHeaders(),

                    body: JSON.stringify({
                        model
                    })
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || "Collection empty operation failed");
            }

            // Refresh statistics after deletion
            await loadModels(false);

            Swal.fire({
                icon: "success",
                title: "Collection Empty",
                text: data.message || "Collection emptied successfully.",
                timer: 2000,
                showConfirmButton: false
            });

        } catch (error) {

            console.error(error);

            Swal.fire(
                "Error",
                error.message || "Operation failed",
                "error"
            );

        } finally {

            setResetting(false);

        }
    };

    // =====================================================
    // EMPTY ALL COLLECTIONS
    // POST /api/danger-zone/empty-all
    // =====================================================

    const handleEmptyAll = async () => {

        const result = await Swal.fire({

            title: "EXTREME DANGER",

            html: `
                <div style="text-align:left">

                    <p>
                        This operation will permanently delete
                        <strong> ALL DATA </strong>
                        from all configured collections.
                    </p>

                    <p style="color:#dc3545;font-weight:bold">
                        This operation cannot be undone.
                    </p>

                    <h5 style="text-align:center">
                        DELETE ALL DATA
                    </h5>

                </div>
            `,

            icon: "error",

            input: "text",

            inputLabel: "Type DELETE ALL DATA to confirm",

            inputPlaceholder: "DELETE ALL DATA",

            showCancelButton: true,

            confirmButtonText: "RESET DATABASE",

            confirmButtonColor: "#b02a37",

            cancelButtonText: "Cancel",

            inputValidator: (value) => {

                if (value !== "DELETE ALL DATA") {
                    return "Confirmation text does not match";
                }

                return null;
            }

        });

        if (!result.isConfirmed) return;

        try {

            setResetting(true);

            const response = await fetch(
                `${API_URL}/danger-zone/empty-all`,
                {
                    method: "POST",
                    headers: getHeaders()
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || "Database reset failed");
            }

            // Refresh statistics after reset
            await loadModels(false);

            Swal.fire({
                icon: "success",
                title: "Database Reset",
                text: data.message || "All configured collections have been emptied.",
                confirmButtonColor: "#198754"
            });

        } catch (error) {

            console.error(error);

            Swal.fire(
                "Reset Failed",
                error.message || "Unable to reset database",
                "error"
            );

        } finally {

            setResetting(false);

        }
    };

    // =====================================================
    // REFRESH BUTTON
    // =====================================================

    const handleRefresh = () => {
        loadModels();
    };

    // =====================================================
    // RENDER COMPONENT
    // =====================================================

    return (

        <div className="container-fluid py-4">

            {/* PAGE HEADER */}

            <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">

                <div>
                    <h4 className="fw-bold mb-1">
                        Database Management
                    </h4>

                    <p className="text-muted mb-0">
                        MongoDB statistics, backup and maintenance
                    </p>
                </div>

                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleRefresh}
                    disabled={loading || resetting || !!exporting}
                >
                    <i className="bi bi-arrow-clockwise me-2"></i>

                    {loading ? "Refreshing..." : "Refresh Database"}
                </button>

            </div>

            {/* DATABASE STATISTICS */}

            <div className="row mb-4">

                {/* TOTAL COLLECTIONS */}

                <div className="col-md-4 mb-3">

                    <div className="card shadow-sm border-primary h-100">

                        <div className="card-body">

                            <div className="d-flex justify-content-between align-items-center">

                                <div>

                                    <small className="text-muted">
                                        Total Collections
                                    </small>

                                    <h3 className="fw-bold text-primary mb-0">
                                        {summary.totalCollections.toLocaleString()}
                                    </h3>

                                </div>

                                <i className="bi bi-collection fs-1 text-primary"></i>

                            </div>

                        </div>

                    </div>

                </div>

                {/* TOTAL DOCUMENTS */}

                <div className="col-md-4 mb-3">

                    <div className="card shadow-sm border-success h-100">

                        <div className="card-body">

                            <div className="d-flex justify-content-between align-items-center">

                                <div>

                                    <small className="text-muted">
                                        Total Documents
                                    </small>

                                    <h3 className="fw-bold text-success mb-0">
                                        {summary.totalDocuments.toLocaleString()}
                                    </h3>

                                </div>

                                <i className="bi bi-file-earmark-text fs-1 text-success"></i>

                            </div>

                        </div>

                    </div>

                </div>

                {/* DATABASE SIZE */}

                <div className="col-md-4 mb-3">

                    <div className="card shadow-sm border-info h-100">

                        <div className="card-body">

                            <div className="d-flex justify-content-between align-items-center">

                                <div>

                                    <small className="text-muted">
                                        Database Size
                                    </small>

                                    <h3 className="fw-bold text-info mb-0">
                                        {formatSize(summary.totalSize)}
                                    </h3>

                                </div>

                                <i className="bi bi-database fs-1 text-info"></i>

                            </div>

                        </div>

                    </div>

                </div>

            </div>

            {/* EXPORT SECTION */}

            <div className="card shadow-sm mb-4">

                <div className="card-header bg-light">

                    <h6 className="fw-bold mb-0">

                        <i className="bi bi-cloud-download me-2"></i>

                        Database Backup & Export

                    </h6>

                </div>

                <div className="card-body">

                    <p className="text-muted">
                        Download a backup of all configured MongoDB collections.
                    </p>

                    <div className="d-flex flex-wrap gap-2">

                        {/* JSON EXPORT */}

                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            disabled={!!exporting || resetting}
                            onClick={() => handleExport("json")}
                        >

                            {exporting === "json" ? (

                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Exporting...
                                </>

                            ) : (

                                <>
                                    <i className="bi bi-filetype-json me-2"></i>
                                    Export JSON
                                </>

                            )}

                        </button>

                        {/* CSV EXPORT */}

                        <button
                            type="button"
                            className="btn btn-outline-success"
                            disabled={!!exporting || resetting}
                            onClick={() => handleExport("csv")}
                        >

                            {exporting === "csv" ? (

                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Exporting...
                                </>

                            ) : (

                                <>
                                    <i className="bi bi-filetype-csv me-2"></i>
                                    Export CSV
                                </>

                            )}

                        </button>

                        {/* ZIP EXPORT */}

                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={!!exporting || resetting}
                            onClick={() => handleExport("zip")}
                        >

                            {exporting === "zip" ? (

                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Creating ZIP...
                                </>

                            ) : (

                                <>
                                    <i className="bi bi-file-earmark-zip me-2"></i>
                                    Export ZIP Backup
                                </>

                            )}

                        </button>

                    </div>

                    <small className="text-muted d-block mt-3">

                        ZIP backup contains individual JSON and CSV files
                        for each configured collection.

                    </small>

                </div>

            </div>

            {/* WARNING */}

            <div className="alert alert-warning">

                <strong>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Warning!
                </strong>

                <p className="mb-0 mt-1">
                    Operations in this section can permanently delete
                    MongoDB records. Always download a backup before
                    performing destructive operations.
                </p>

            </div>

            {/* INDIVIDUAL COLLECTIONS */}

            <div className="card shadow-sm mb-4">

                <div className="card-header bg-light d-flex justify-content-between align-items-center">

                    <h6 className="fw-bold mb-0">
                        <i className="bi bi-collection me-2"></i>
                        Individual Collections
                    </h6>

                    <span className="badge bg-primary">
                        {models.length} Collections
                    </span>

                </div>

                <div className="card-body">

                    {loading && models.length === 0 ? (

                        <div className="text-center py-5">

                            <div className="spinner-border text-primary"></div>

                            <p className="text-muted mt-2">
                                Loading database collections...
                            </p>

                        </div>

                    ) : models.length === 0 ? (

                        <div className="text-center text-muted py-4">

                            <i className="bi bi-database-x fs-1"></i>

                            <p className="mt-2 mb-0">
                                No collections found.
                            </p>

                        </div>

                    ) : (

                        <div className="row">

                            {models.map((item) => (

                                <div
                                    className="col-md-6 col-lg-4 mb-3"
                                    key={item.key}
                                >

                                    <div
                                        className="border rounded p-3 h-100"
                                        style={{
                                            background: "#fffafa"
                                        }}
                                    >

                                        <div className="d-flex justify-content-between align-items-start">

                                            <div className="me-2">

                                                <div className="fw-bold text-dark">
                                                    <i className="bi bi-table me-2 text-primary"></i>
                                                    {item.name}
                                                </div>

                                                <small className="text-muted">
                                                    {item.collectionName || item.key}
                                                </small>

                                            </div>

                                            <span className="badge bg-primary">
                                                {(item.count || 0).toLocaleString()}
                                            </span>

                                        </div>

                                        <hr className="my-2" />

                                        <div className="d-flex justify-content-between align-items-center">

                                            <div>

                                                <small className="text-muted d-block">
                                                    Documents
                                                </small>

                                                <strong>
                                                    {(item.count || 0).toLocaleString()}
                                                </strong>

                                            </div>

                                            <div className="text-end">

                                                <small className="text-muted d-block">
                                                    Collection Size
                                                </small>

                                                <strong>
                                                    {formatSize(item.size)}
                                                </strong>

                                            </div>

                                        </div>

                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm w-100 mt-3"
                                            disabled={resetting || !!exporting}
                                            onClick={() =>
                                                handleEmptyModel(item.key)
                                            }
                                        >

                                            <i className="bi bi-trash me-2"></i>

                                            Empty Collection

                                        </button>

                                    </div>

                                </div>

                            ))}

                        </div>

                    )}

                </div>

            </div>

            {/* RESET ENTIRE DATABASE */}

            <div
                className="card border-danger shadow-sm"
            >

                <div className="card-header bg-danger text-white">

                    <h6 className="fw-bold mb-0">

                        <i className="bi bi-exclamation-octagon me-2"></i>

                        Danger Zone - Reset Entire Database

                    </h6>

                </div>

                <div className="card-body">

                    <div className="row align-items-center">

                        <div className="col-md-8">

                            <h5 className="text-danger fw-bold">

                                Reset Entire Database

                            </h5>

                            <p className="text-muted mb-0">

                                Permanently delete all documents from every
                                collection configured in this Danger Zone.
                                This action cannot be undone.

                            </p>

                            <small className="text-danger fw-semibold">

                                Make sure you have a backup before proceeding.

                            </small>

                        </div>

                        <div className="col-md-4 text-md-end mt-3 mt-md-0">

                            <button
                                type="button"
                                className="btn btn-danger px-4"
                                disabled={resetting || !!exporting}
                                onClick={handleEmptyAll}
                            >

                                {resetting ? (

                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Processing...
                                    </>

                                ) : (

                                    <>
                                        <i className="bi bi-trash3 me-2"></i>
                                        Reset Database
                                    </>

                                )}

                            </button>

                        </div>

                    </div>

                </div>

            </div>

        </div>

    );

};

export default DangerZone;
